import { useEffect, useState } from "react";
import { fetchMark } from "../api/client";

/**
 * The certification body's stored mark, as a URL the browser can draw.
 *
 * Fetched rather than pointed at, for the reason `fetchMark` gives: the endpoint is
 * authenticated because a body's mark is its own, and an `<img src>` is a plain browser
 * request carrying no `Authorization` header. The bytes come back through the same client
 * as everything else and become an object URL.
 *
 * **Revoked on unmount and before every replacement.** An object URL holds the blob alive
 * until it is released, and a shell that re-reads the mark on each navigation would leak
 * one image per move — over a session that runs for hours, which M4's does, that is not a
 * rounding error.
 *
 * A body with no mark yet is not an error state. The upload is optional at onboarding and
 * the answer is simply `null`, which the shell draws as Complao's own tile.
 */
export function useBrandMark(tenantId: string): string | null {
  const [mark, setMark] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    let issued: string | null = null;

    fetchMark(tenantId)
      .then((url) => {
        if (!live) {
          // Unmounted while the bytes were in flight — release rather than orphan.
          URL.revokeObjectURL(url);
          return;
        }
        issued = url;
        setMark(url);
      })
      .catch(() => undefined);

    return () => {
      live = false;
      if (issued) URL.revokeObjectURL(issued);
    };
  }, [tenantId]);

  return mark;
}
